import { SHIP_RADIUS, SPAWN_POINT_RADIUS } from "../constants";
import { wrappedDistance } from "../math/torus";
import { parseJsonMap } from "./json-map";
import { type MapDef, MapFormatError } from "./map-def";

/**
 * Bounds for player-made maps (P11.1). Built-in maps sit well inside them. They keep custom maps
 * playable (spawn points clear of planets), small enough to send to an online room (the relay
 * accepts 8 KB frames) and cheap to simulate.
 */
export const CUSTOM_MAP_LIMITS = {
	nameLength: 32,
	worldMin: 1500,
	worldMax: 12000,
	planets: 24,
	spawnPoints: 8,
	fuelItems: 16,
	radiusMin: 20,
	radiusMax: 700,
	massMin: 10,
	massMax: 3000,
	fuelRateMin: 1,
	fuelRateMax: 10,
	asteroidsMax: 50,
	asteroidsPerMinuteMax: 60,
	asteroidSpeedMin: 0.5,
	asteroidSpeedMax: 12,
} as const;

/** Space a ship needs around a spawn point before it touches a planet. */
export const SPAWN_CLEARANCE = SPAWN_POINT_RADIUS + SHIP_RADIUS;

/**
 * Everything wrong with a map as a custom map, in plain words (empty = fine). Distances are
 * measured across the wrap seam, like the game does with seamless space.
 */
export function customMapProblems(map: MapDef): string[] {
	const L = CUSTOM_MAP_LIMITS;
	const problems: string[] = [];
	const size = { width: map.size.w, height: map.size.h };
	if (map.name.trim().length === 0) problems.push("The map needs a name.");
	if (map.name.length > L.nameLength)
		problems.push(`The name is longer than ${L.nameLength} characters.`);
	for (const [label, v] of [
		["width", map.size.w],
		["height", map.size.h],
	] as const) {
		if (v < L.worldMin || v > L.worldMax) {
			problems.push(`The world ${label} must be between ${L.worldMin} and ${L.worldMax}.`);
		}
	}
	if (map.planets.length > L.planets) problems.push(`At most ${L.planets} planets.`);
	if (map.spawnPoints.length === 0) problems.push("At least one spawn point is needed.");
	if (map.spawnPoints.length > L.spawnPoints)
		problems.push(`At most ${L.spawnPoints} spawn points.`);
	if (map.fuelItems.length > L.fuelItems) problems.push(`At most ${L.fuelItems} fueling stations.`);
	map.planets.forEach((p, i) => {
		const name = `Planet ${i + 1}`;
		if (p.radius < L.radiusMin || p.radius > L.radiusMax) {
			problems.push(`${name}: radius must be between ${L.radiusMin} and ${L.radiusMax}.`);
		}
		if (p.mass < L.massMin || p.mass > L.massMax) {
			problems.push(`${name}: mass must be between ${L.massMin} and ${L.massMax}.`);
		}
		for (let j = 0; j < i; j++) {
			const q = map.planets[j];
			if (q && wrappedDistance(p, q, size) < p.radius + q.radius) {
				problems.push(`${name} overlaps planet ${j + 1}.`);
			}
		}
	});
	map.spawnPoints.forEach((s, i) => {
		const hit = map.planets.findIndex(
			(p) => wrappedDistance(s, p, size) < p.radius + SPAWN_CLEARANCE,
		);
		if (hit >= 0) problems.push(`Spawn point ${i + 1} is too close to planet ${hit + 1}.`);
	});
	map.fuelItems.forEach((f, i) => {
		if (f.amount < L.fuelRateMin || f.amount > L.fuelRateMax) {
			problems.push(
				`Fueling station ${i + 1}: refuel rate must be between ${L.fuelRateMin} and ${L.fuelRateMax}.`,
			);
		}
	});
	const a = map.asteroids;
	if (a?.max !== undefined && a.max > L.asteroidsMax)
		problems.push(`At most ${L.asteroidsMax} asteroids at once.`);
	if (a?.perMinute !== undefined && a.perMinute > L.asteroidsPerMinuteMax)
		problems.push(`At most ${L.asteroidsPerMinuteMax} asteroids per minute.`);
	if (a?.speed !== undefined && (a.speed < L.asteroidSpeedMin || a.speed > L.asteroidSpeedMax))
		problems.push(
			`Asteroid speed must be between ${L.asteroidSpeedMin} and ${L.asteroidSpeedMax}.`,
		);
	return problems;
}

/**
 * Parses untrusted JSON (a file, storage, the network) as a custom map: the JSON map format plus
 * the custom-map limits. Throws MapFormatError with every problem listed.
 */
export function parseCustomMap(data: unknown): MapDef {
	const map = parseJsonMap(data);
	const problems = customMapProblems(map);
	if (problems.length > 0) throw new MapFormatError(problems.join(" "));
	return map;
}
